import React from 'react';
import { Link } from 'react-router-dom';
import { highlightMatch, truncateText } from '../utils/searchUtils';
import { searchCategories } from '../data/searchData';

const SearchResults = ({ 
  results, 
  query, 
  onResultClick, 
  selectedIndex, 
  onClose 
}) => {
  // Group results by category
  const groupedResults = results.reduce((acc, result, index) => {
    if (!acc[result.category]) {
      acc[result.category] = [];
    }
    acc[result.category].push({ ...result, originalIndex: index });
    return acc;
  }, {});

  const handleResultClick = (result) => {
    if (onResultClick) {
      onResultClick(result);
    }
    if (onClose) {
      onClose();
    }
  };

  const handleKeyDown = (event, result) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleResultClick(result);
    }
  };

  if (results.length === 0) {
    return (
      <div className="search-results">
        <div className="search-no-results">
          <div className="no-results-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
              <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2"/>
              <circle cx="11" cy="11" r="3" stroke="currentColor" strokeWidth="1" opacity="0.5"/>
            </svg>
          </div>
          <h3>No results found</h3>
          <p>Try searching for something else or browse our portfolio</p>
          <div className="popular-suggestions">
            <span>Popular searches:</span>
            <div className="suggestion-tags">
              {['Software Development', 'Cyber Security', 'Immigration', 'Contact'].map(suggestion => (
                <button 
                  key={suggestion}
                  className="suggestion-tag"
                  onClick={() => onResultClick && onResultClick({ query: suggestion })}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="search-results">
      {Object.entries(groupedResults).map(([category, categoryResults]) => (
        <div key={category} className="search-category">
          <div className="search-category-header">
            <h3 className="search-category-title">
              {searchCategories[category] || category}
            </h3>
            <span className="search-category-count">
              {categoryResults.length} result{categoryResults.length !== 1 ? 's' : ''}
            </span>
          </div>
          
          <div className="search-category-results">
            {categoryResults.map((result) => (
              <Link
                key={result.id}
                to={result.route}
                className={`search-result-item ${
                  selectedIndex === result.originalIndex ? 'selected' : ''
                }`}
                onClick={() => handleResultClick(result)}
                onKeyDown={(e) => handleKeyDown(e, result)}
                tabIndex={0}
              >
                <div className="search-result-content">
                  <div className="search-result-header">
                    <h4 
                      className="search-result-title"
                      dangerouslySetInnerHTML={{
                        __html: highlightMatch(result.title, query)
                      }}
                    />
                    <div className="search-result-category">
                      {result.category}
                    </div>
                  </div>
                  
                  <p 
                    className="search-result-description"
                    dangerouslySetInnerHTML={{
                      __html: highlightMatch(truncateText(result.description, 120), query)
                    }}
                  />
                  
                  {result.keywords && result.keywords.some(keyword => 
                    keyword.toLowerCase().includes(query.toLowerCase())
                  ) && (
                    <div className="search-result-keywords">
                      {result.keywords
                        .filter(keyword => keyword.toLowerCase().includes(query.toLowerCase()))
                        .slice(0, 3)
                        .map(keyword => (
                          <span 
                            key={keyword}
                            className="search-result-keyword"
                            dangerouslySetInnerHTML={{
                              __html: highlightMatch(keyword, query)
                            }}
                          />
                        ))
                      }
                    </div>
                  )}
                </div>
                
                <div className="search-result-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </Link>
            ))}
          </div>
        </div>
      ))}
      
      <div className="search-results-footer">
        <p className="search-results-tip">
          Press <kbd>↑</kbd> <kbd>↓</kbd> to navigate • <kbd>Enter</kbd> to select • <kbd>Esc</kbd> to close
        </p>
      </div>
    </div>
  );
};

export default SearchResults;